import React, { useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Card from "./Card";
import Dialog from "./Dialog";

export default function DayCard() {
  const [baseDate, setBaseDate] = useState(() => new Date());
  const [isEditOpen, setIsEditOpen] = useState(false);

  function shift(days) {
    const newDate = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate() + days);
    setBaseDate(newDate);
  }

  function setToday() {
    const today = new Date();
    setBaseDate(today);
    setIsEditOpen(false);
  }

  function selectDate(day) {
    const newDate = new Date(baseDate.getFullYear(), baseDate.getMonth(), day);
    setBaseDate(newDate);
  }

  function changeMonth(monthOffset) {
    const newDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + monthOffset, 1);
    setBaseDate(newDate);
  }

  // Get calendar data
  const weekday = baseDate.toLocaleDateString(undefined, { weekday: "long" });
  const month = baseDate.toLocaleDateString(undefined, { month: "long" });
  const year = baseDate.getFullYear();
  const currentDay = baseDate.getDate();

  // Get first day of month and number of days
  const firstDayOfMonth = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
  const lastDayOfMonth = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0);
  const startingDayOfWeek = firstDayOfMonth.getDay(); // 0 = Sunday
  const daysInMonth = lastDayOfMonth.getDate();

  // Create calendar grid
  const calendarDays = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  return (
    <>
      <Card title="Calendar" colorIndex={2} onEdit={() => setIsEditOpen(true)}>
        <div className="h-full flex flex-col min-h-0">
          {/* Header with month/year and navigation */}
          <div className="flex items-center justify-between mb-3 lg:mb-4 flex-shrink-0">
            <button 
              onClick={() => changeMonth(-1)}
              className="p-1 lg:p-2 hover:bg-white/50 rounded-lg transition"
            >
              <ArrowLeft className="w-4 h-4 lg:w-5 lg:h-5" />
            </button>
            <div className="text-center">
              <div className="text-lg lg:text-xl xl:text-2xl 2xl:text-3xl font-fredoka font-bold">
                {month} {year}
              </div>
              <div className="text-sm lg:text-base font-fredoka font-medium text-slate-600">
                {weekday}
              </div>
            </div>
            <button 
              onClick={() => changeMonth(1)}
              className="p-1 lg:p-2 hover:bg-white/50 rounded-lg transition"
            >
              <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5" />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2 flex-shrink-0">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((dayName) => (
                <div key={dayName} className="text-center text-xs lg:text-sm font-fredoka font-bold text-slate-600 py-1">
                  {dayName}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-1 flex-1 auto-rows-fr">
              {calendarDays.map((day, index) => (
                <button
                  key={index}
                  onClick={() => day && selectDate(day)}
                  disabled={!day}
                  className={`
                    flex items-center justify-center text-sm lg:text-base xl:text-lg font-fredoka font-bold rounded-lg transition-all
                    ${!day ? 'invisible' : ''}
                    ${day === currentDay 
                      ? 'bg-pink-400 text-white shadow-lg ring-2 ring-pink-200' 
                      : 'bg-white/70 text-slate-800 hover:bg-white/90 shadow'
                    }
                  `}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <Dialog isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title="Edit Calendar">
        <div className="space-y-4">
          <div className="text-center">
            <div className="text-2xl font-fredoka font-bold mb-1">{weekday}</div>
            <div className="text-lg font-fredoka font-medium text-slate-600">{month} {currentDay}, {year}</div>
          </div>

          <div>
            <h3 className="font-fredoka font-bold mb-2">Navigate Days</h3>
            <div className="flex items-center justify-center gap-4">
              <button 
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-pink-200 shadow font-fredoka font-bold hover:bg-pink-300 transition" 
                onClick={() => shift(-1)}
              >
                <ArrowLeft className="w-4 h-4" />
                Previous Day
              </button>
              <button 
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-pink-200 shadow font-fredoka font-bold hover:bg-pink-300 transition" 
                onClick={() => shift(1)}
              >
                Next Day
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div>
            <h3 className="font-fredoka font-bold mb-2">Navigate Months</h3>
            <div className="flex items-center justify-center gap-4">
              <button 
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-200 shadow font-fredoka font-bold hover:bg-blue-300 transition" 
                onClick={() => changeMonth(-1)}
              >
                <ArrowLeft className="w-4 h-4" />
                Previous Month
              </button>
              <button 
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-200 shadow font-fredoka font-bold hover:bg-blue-300 transition" 
                onClick={() => changeMonth(1)}
              >
                Next Month
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div>
            <h3 className="font-fredoka font-bold mb-2">Quick Navigation</h3>
            <div className="grid grid-cols-2 gap-2">
              <button 
                className="px-3 py-2 rounded-lg bg-purple-200 font-fredoka font-bold shadow hover:bg-purple-300 transition" 
                onClick={() => shift(-7)}
              >
                -1 Week
              </button>
              <button 
                className="px-3 py-2 rounded-lg bg-purple-200 font-fredoka font-bold shadow hover:bg-purple-300 transition" 
                onClick={() => shift(7)}
              >
                +1 Week
              </button>
              <button 
                className="px-3 py-2 rounded-lg bg-green-200 font-fredoka font-bold shadow hover:bg-green-300 transition" 
                onClick={() => shift(-30)}
              >
                -1 Month
              </button>
              <button 
                className="px-3 py-2 rounded-lg bg-green-200 font-fredoka font-bold shadow hover:bg-green-300 transition" 
                onClick={() => shift(30)}
              >
                +1 Month
              </button>
            </div>
          </div>

          <div>
            <button 
              className="w-full px-3 py-2 rounded-lg bg-orange-200 font-fredoka font-bold shadow hover:bg-orange-300 transition" 
              onClick={setToday}
            >
              Go to Today
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
